import React, { useState, useEffect } from 'react';
import { utils as xlsxUtils, writeFile } from 'xlsx';
import ExcelExportService from '../../services/ExcelExportService';
import { useAuth } from 'react-oidc-context';
import './DownloadModal.scss';

interface DownloadModalProps {
  isOpen: boolean;
  onClose: () => void;
  activeTab: string;
}

interface DownloadOption {
  id: string;
  label: string;
  description: string;
  fileName: string;
  sheetName: string;
  getData: (token: string) => Promise<any[]>;
}

const DownloadModal: React.FC<DownloadModalProps> = ({
  isOpen,
  onClose,
  activeTab
}) => {
  const auth = useAuth();
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string>('');
  const [downloadError, setDownloadError] = useState<string | null>(null);

  
  // Get the yyy-MM-dd for the current date.
  const currentDate = (new Date()).toISOString().split('T')[0];

  const downloadOptions: DownloadOption[] = [
    {
      id: 'flightRanges',
      label: 'Flight Ranges Data',
      description: 'Export all flight range information including effective and discontinued dates',
      fileName: `AAG Flight Range changes for ${currentDate}.xlsx`,
      sheetName: 'Flight Ranges',
      getData: ExcelExportService.getFlightRangeExportData
    },
    {
      id: 'partners',
      label: 'Partners Data',
      description: 'Export partner airlines information and relationships',
      fileName: `AAG Partners List ${currentDate}.xlsx`,
      sheetName: 'Partners',
      getData: ExcelExportService.getPartnersExportData
    }
  ];

  useEffect(() => {
    if (isOpen && !selectedOption) {
      setSelectedOption(activeTab === 'partners' ? 'partners' : 'flightRanges');
    }
  }, [isOpen, activeTab, selectedOption]);

  useEffect(() => {
    if (!isOpen) {
      setDownloadError(null);
      setSelectedOption('');
    }
  }, [isOpen]);

  const handleDownload = async () => {
    if (!selectedOption) return;

    try {
      setIsDownloading(true);
      setDownloadError(null);

      const token = auth.user?.access_token;
      if (!token) {
        throw new Error('Authentication token is not available. Please log in again.');
      }

      const option = downloadOptions.find(opt => opt.id === selectedOption);
      if (!option) {
        throw new Error('Invalid download option selected.');
      }

      const data = await option.getData(token);
      
      if (!Array.isArray(data) || data.length === 0) {
        throw new Error(`No ${option.label.toLowerCase()} available to export.`);
      }

      const ws = xlsxUtils.json_to_sheet(data);
      const wb = xlsxUtils.book_new();
      xlsxUtils.book_append_sheet(wb, ws, option.sheetName);
      writeFile(wb, option.fileName);

      // Close modal after successful download
      setTimeout(() => {
        onClose();
      }, 500);

    } catch (error: any) {
      console.error('Error downloading data:', error);
      setDownloadError(error.message || 'Failed to download data. Please try again later.');
    } finally {
      setIsDownloading(false);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Escape') {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="download-modal-overlay"
      onClick={onClose}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="download-modal-title"
      aria-describedby="download-modal-description"
    >
      <div 
        className="download-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="download-modal__header">
          <h2 id="download-modal-title" className="download-modal__title">
            Download Data
          </h2>
          <button
            className="download-modal__close"
            onClick={onClose}
            aria-label="Close download modal"
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 4L4 12M4 4L12 12" stroke="#666" strokeWidth="2" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        <div className="download-modal__content">
          <p id="download-modal-description" className="download-modal__description">
            Select the data you want to export to Excel format.
          </p>

          <div className="download-modal__options">
            {downloadOptions.map((option) => (
              <label
                key={option.id}
                className={`download-modal__option ${
                  selectedOption === option.id ? 'download-modal__option--selected' : ''
                }`}
              >
                <input
                  type="radio"
                  name="downloadOption"
                  value={option.id}
                  checked={selectedOption === option.id}
                  onChange={(e) => setSelectedOption(e.target.value)}
                  className="download-modal__radio"
                  disabled={isDownloading}
                />
                <div className="download-modal__option-content">
                  <div className="download-modal__option-label">{option.label}</div>
                  <div className="download-modal__option-description">{option.description}</div>
                </div>
              </label>
            ))}
          </div>

          {downloadError && (
            <div 
              className="download-modal__error" 
              role="alert" 
              aria-live="assertive"
            >
              {downloadError}
            </div>
          )}
        </div>

        <div className="download-modal__footer">
          <button
            className="download-modal__cancel"
            onClick={onClose}
            disabled={isDownloading}
            type="button"
          >
            Cancel
          </button>
          <button
            className="download-modal__download"
            onClick={handleDownload}
            disabled={isDownloading || !selectedOption}
            aria-busy={isDownloading}
            type="button"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 5H10V1H6V5H2L8 11L14 5ZM2 13V15H14V13H2Z" fill="currentColor"/>
            </svg>
            {isDownloading ? 'Downloading...' : 'Download Excel File'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DownloadModal;
